import { AppConstants } from '@/app.constants';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import {
  FactoryVisitSession,
  FactoryVisitSessionEvidence,
  LocationEvidence,
} from '@/types/LocationVerification.type';
import {
  isFactoryVisitSessionReuseEnabled,
  isSessionDateInReuseWindow,
  pickBestReusableSession,
  toLocalDateString,
} from '@/utils/factoryVisitSession.util';
import { getInspectorTimeZone } from '@/utils/inspectorTimeZone.util';
import { ensureDB } from './idbStorage';

const SESSION_STORE = AppConstants.INDEX_DB.store.factoryVisitSessions;
const IMAGE_STORE = AppConstants.INDEX_DB.store.images;
const REUSE_DAYS_CACHE_PREFIX = 'factoryVisitSessionReuseDays';

function buildSessionImageKey(factoryId: string, evidenceId: string): string {
  return `${factoryId}_${InspectionImageType.VERIFICATION}_${IMAGE_STORE}_${evidenceId}`;
}

function buildInspectorSessionPrefix(params: {
  tenantId: string;
  factoryId: string;
  inspectorId: string;
}): string {
  return `${params.tenantId}_${params.factoryId}_${params.inspectorId}_`;
}

function buildReuseDaysCacheKey(tenantId: string): string {
  return `${REUSE_DAYS_CACHE_PREFIX}_${tenantId}`;
}

export function buildFactoryVisitSessionLocalId(params: {
  tenantId: string;
  factoryId: string;
  inspectorId: string;
  sessionDate?: string;
  inspectorTimeZone?: string;
}): string {
  const inspectorTimeZone = params.inspectorTimeZone ?? getInspectorTimeZone();
  const sessionDate = params.sessionDate ?? toLocalDateString(inspectorTimeZone);
  return `${params.tenantId}_${params.factoryId}_${params.inspectorId}_${sessionDate}`;
}

export const factoryVisitSessionStorage = {
  get: async (sessionId: string): Promise<FactoryVisitSession | null> => {
    const db = await ensureDB();
    const record = await db.get(SESSION_STORE, sessionId);
    return record ?? null;
  },

  listSessionsForInspector: async (params: {
    tenantId: string;
    factoryId: string;
    inspectorId: string;
  }): Promise<FactoryVisitSession[]> => {
    const db = await ensureDB();
    const prefix = buildInspectorSessionPrefix(params);
    const allSessions = (await db.getAll(SESSION_STORE)) as FactoryVisitSession[];
    return allSessions.filter(
      (session) =>
        session.tenantId === params.tenantId &&
        session.factoryId === params.factoryId &&
        session.inspectorId === params.inspectorId &&
        session.id.startsWith(prefix)
    );
  },

  findReusableSessionForFactory: async (params: {
    tenantId: string;
    factoryId: string;
    inspectorId: string;
    reuseDays: number;
  }): Promise<FactoryVisitSession | null> => {
    if (!isFactoryVisitSessionReuseEnabled(params.reuseDays)) {
      return null;
    }
    const inspectorTimeZone = getInspectorTimeZone();
    const sessions = await factoryVisitSessionStorage.listSessionsForInspector(params);
    const eligibleSessions = sessions.filter(
      (session) =>
        session.status === 'ACTIVE' &&
        isSessionDateInReuseWindow(session.sessionDate, params.reuseDays, inspectorTimeZone)
    );
    return pickBestReusableSession(eligibleSessions);
  },

  getActiveForFactory: async (params: {
    tenantId: string;
    factoryId: string;
    inspectorId: string;
    reuseDays?: number;
  }): Promise<FactoryVisitSession | null> => {
    const reuseDays =
      params.reuseDays ?? factoryVisitSessionStorage.getCachedReuseDays(params.tenantId);
    return factoryVisitSessionStorage.findReusableSessionForFactory({
      tenantId: params.tenantId,
      factoryId: params.factoryId,
      inspectorId: params.inspectorId,
      reuseDays,
    });
  },

  cacheReuseDays: (tenantId: string, reuseDays: number): void => {
    localStorage.setItem(buildReuseDaysCacheKey(tenantId), String(reuseDays));
  },

  getCachedReuseDays: (tenantId: string): number => {
    const raw = localStorage.getItem(buildReuseDaysCacheKey(tenantId));
    if (raw == null || raw === '') {
      return 1;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 1;
  },

  put: async (session: FactoryVisitSession): Promise<void> => {
    const db = await ensureDB();
    await db.put(SESSION_STORE, { ...session, updatedAt: new Date().toISOString() });
  },

  saveSessionEvidenceImage: async (params: {
    factoryId: string;
    clientEvidenceId: string;
    imageData: string;
  }): Promise<void> => {
    const db = await ensureDB();
    const imageKey = buildSessionImageKey(params.factoryId, params.clientEvidenceId);
    await db.put(IMAGE_STORE, params.imageData, imageKey);
  },

  getSessionImageData: async (
    factoryId: string,
    clientEvidenceId: string
  ): Promise<string | null> => {
    const db = await ensureDB();
    const imageKey = buildSessionImageKey(factoryId, clientEvidenceId);
    return (await db.get(IMAGE_STORE, imageKey)) ?? null;
  },

  upsertFromServer: async (params: {
    tenantId: string;
    inspectorId: string;
    serverSession: {
      id: string;
      factoryId: string;
      inspectorId: string;
      sessionDate: string;
      status: FactoryVisitSession['status'];
      evidence: FactoryVisitSessionEvidence[];
    };
  }): Promise<FactoryVisitSession> => {
    const localId = buildFactoryVisitSessionLocalId({
      tenantId: params.tenantId,
      factoryId: params.serverSession.factoryId,
      inspectorId: params.inspectorId,
      sessionDate: params.serverSession.sessionDate,
    });
    const session: FactoryVisitSession = {
      id: localId,
      serverSessionId: params.serverSession.id,
      tenantId: params.tenantId,
      factoryId: params.serverSession.factoryId,
      inspectorId: params.inspectorId,
      sessionDate: params.serverSession.sessionDate,
      status: params.serverSession.status,
      evidence: params.serverSession.evidence,
      updatedAt: new Date().toISOString(),
    };
    await factoryVisitSessionStorage.put(session);
    return session;
  },

  addCapturedEvidence: async (params: {
    session: FactoryVisitSession;
    evidence: LocationEvidence;
    imageData?: string;
    sourceInspectionId?: string;
  }): Promise<FactoryVisitSession> => {
    if (params.imageData) {
      await factoryVisitSessionStorage.saveSessionEvidenceImage({
        factoryId: params.session.factoryId,
        clientEvidenceId: params.evidence.clientEvidenceId,
        imageData: params.imageData,
      });
    }
    const sessionEvidence: FactoryVisitSessionEvidence = {
      clientEvidenceId: params.evidence.clientEvidenceId,
      role: params.evidence.role,
      latitude: params.evidence.latitude,
      longitude: params.evidence.longitude,
      accuracy: params.evidence.accuracy,
      altitude: params.evidence.altitude,
      altitudeAccuracy: params.evidence.altitudeAccuracy,
      heading: params.evidence.heading,
      speed: params.evidence.speed,
      gpsCaptureSource: params.evidence.gpsCaptureSource,
      gpsAccuracyPreciseEnough: params.evidence.gpsAccuracyPreciseEnough,
      connectionType: params.evidence.connectionType,
      connectionEffectiveType: params.evidence.connectionEffectiveType,
      geolocationPermissionState: params.evidence.geolocationPermissionState,
      clientUserAgent: params.evidence.clientUserAgent,
      gpsCapturedAt: params.evidence.gpsCapturedAt,
      imageUrl: params.evidence.imageUrl,
      sourceInspectionId: params.sourceInspectionId,
      contentType: params.evidence.contentType,
    };
    const existingIndex = params.session.evidence.findIndex(
      (item) =>
        item.role === params.evidence.role &&
        item.clientEvidenceId === params.evidence.clientEvidenceId
    );
    const nextEvidence =
      existingIndex >= 0
        ? params.session.evidence.map((item, index) =>
            index === existingIndex ? sessionEvidence : item
          )
        : [...params.session.evidence, sessionEvidence];
    const nextSession: FactoryVisitSession = {
      ...params.session,
      evidence: nextEvidence,
      updatedAt: new Date().toISOString(),
    };
    await factoryVisitSessionStorage.put(nextSession);
    return nextSession;
  },

  removeCapturedEvidence: async (params: {
    session: FactoryVisitSession;
    clientEvidenceId: string;
  }): Promise<FactoryVisitSession> => {
    const db = await ensureDB();
    const tx = db.transaction([SESSION_STORE, IMAGE_STORE], 'readwrite');
    const imageKey = buildSessionImageKey(params.session.factoryId, params.clientEvidenceId);
    await tx.objectStore(IMAGE_STORE).delete(imageKey);
    const nextSession: FactoryVisitSession = {
      ...params.session,
      evidence: params.session.evidence.filter(
        (item) => item.clientEvidenceId !== params.clientEvidenceId
      ),
      updatedAt: new Date().toISOString(),
    };
    await tx.objectStore(SESSION_STORE).put(nextSession);
    await tx.done;
    return nextSession;
  },
};
